import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  BookOpen,
  ChevronDown,
  Clock3,
  GitBranch,
  LoaderCircle,
  Plus,
  Settings2,
  Target,
  X,
  Lightbulb,
  ArrowLeft,
} from "lucide-react";
import type { Square } from "chess.js";
import {
  emptyState,
  type AppState,
  type Message,
  type ProviderStatus,
} from "../shared/types";
import { replay } from "../shared/chess";
import {
  api,
  ApiError,
  cloud,
  initializeCloud,
  cancelRequest,
  type Identity,
} from "./lib/api";
import { readOutbox, writeOutbox } from "./lib/outbox";
import { useLiveCoach } from "./lib/useLiveCoach";
import { ActionQueue, type QueueStatus } from "./lib/action-queue";
import { Composer, CoachMark } from "./components/Composer";
const Workspace = lazy(() =>
  import("./components/Workspace").then((module) => ({
    default: module.Workspace,
  })),
);
import { useLichess } from "./lib/useLichess";
import { ImportDialog } from "./components/ImportDialog";
export default function App() {
  const [state, setState] = useState<AppState>(emptyState);
  const queue = useRef<ActionQueue | null>(null);
  const initialization = useRef<Promise<void>>(Promise.resolve());
  const workspaceKey = useRef("");
  const [saveStatus, setSaveStatus] = useState<QueueStatus>({
    pending: 0,
    saving: false,
    error: "",
    computing: null,
  });
  const pendingRemote = useRef<Promise<unknown>>(Promise.resolve());
  const remoteSavingRef = useRef(false);
  const [remoteSaving, setRemoteSaving] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const [provider, setProvider] = useState<ProviderStatus>({
    configured: false,
    provider: "OpenAI",
    model: "",
    transcription: false,
  });
  const [identity, setIdentity] = useState<Identity>({
    storage: "local",
    email: null,
    anonymous: true,
  });
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [selected, setSelected] = useState<Square | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const [connectionError, setConnectionError] = useState("");
  const controller = useRef<AbortController | null>(null);
  const background = useRef<AbortController | null>(null);
  const opponentKey = useRef("");
  const actionLock = useRef(false);
  const foregroundSequence = useRef(0);
  const activeRequest = useRef<string | null>(null);
  const pendingCancellation = useRef<Promise<void>>(Promise.resolve());
  const [reconnectEpoch, setReconnectEpoch] = useState(0);
  const draftRequest = useRef<{ text: string; id: string } | null>(null);
  const main = useRef<HTMLElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const notify = useCallback((text: string) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 7000);
  }, []);
  const publish = useCallback((next: AppState) => {
    stateRef.current = next;
    setState(next);
  }, []);
  const accept = useCallback(
    (next: AppState, force = false) => {
      if (queue.current) void queue.current.replace(next, force);
      else publish(next);
    },
    [publish],
  );
  const remoteRequest = useCallback(
    async (
      path: string,
      body: unknown,
      signal: AbortSignal,
      applied: (state: AppState) => void,
    ) => {
      let shown = false;
      try {
        return await api(path, body, signal, (next) => {
          shown = true;
          remoteSavingRef.current = true;
          setRemoteSaving(true);
          applied(next);
        });
      } catch (error) {
        // A computed preview is not a confirmed save. Reconcile before releasing
        // dependent queued actions, including when the final response was lost.
        if (shown || (error instanceof ApiError && error.status === 409)) {
          try {
            const result = await api("state");
            await queue.current?.replace(result.state, true);
          } catch {
            setConnectionError(
              "The result could not be confirmed. Reconnect to recover your saved workspace.",
            );
          }
        }
        throw error;
      } finally {
        remoteSavingRef.current = false;
        setRemoteSaving(false);
      }
    },
    [],
  );
  const flush = useCallback(async () => {
    await pendingRemote.current.catch(() => {});
    await queue.current?.flush();
  }, []);
  const refresh = useCallback(async () => {
    await flush();
    let response;
    try {
      response = await api("state");
    } catch (error) {
      if (
        !(error instanceof ApiError) ||
        error.code !== "EMAIL_VERIFICATION_REQUIRED"
      )
        throw error;
      await cloud?.auth.signOut({ scope: "local" });
      response = await api("state");
      notify("Please sign in again and verify your email code.");
    }
    const scope = `${response.identity?.storage}:${response.identity?.id ?? response.identity?.email ?? "session"}`;
    const newScope = !queue.current || scope !== workspaceKey.current;
    const journal = newScope ? await readOutbox(scope) : undefined;
    if (newScope) {
      workspaceKey.current = scope;
      queue.current = new ActionQueue(
        response.state,
        {
          send: async (
            actions,
            expectedRevision,
            requestId,
            signal,
            applied,
          ) => {
            await pendingCancellation.current;
            await pendingRemote.current.catch(() => {});
            signal.throwIfAborted();
            return api(
              "actions",
              { actions, expectedRevision, requestId },
              signal,
              applied,
            );
          },
          load: async () => (await api("state")).state,
          cancel: cancelRequest,
        },
        publish,
        setSaveStatus,
        notify,
        (journal) => {
          void writeOutbox(scope, journal);
        },
      );
    }
    if (journal) await queue.current!.restore(journal);
    else await queue.current!.replace(response.state, true);
    if (response.identity) setIdentity(response.identity);
    if (response.provider) setProvider(response.provider);
    setConnectionError("");
    setLocalMessages([]);
    setReconnectEpoch((value) => value + 1);
  }, [flush, publish, notify]);
  const lichess = useLichess(state, identity, ready, accept, flush, notify);
  useEffect(() => {
    let mounted = true;
    initialization.current = initializeCloud()
      .then(async (config) => {
        if (!mounted) return;
        setProvider(config.provider);
        await refresh();
        setReady(true);
      })
      .catch((e) => {
        setConnectionError(e.message);
        setReady(true);
      });
    return () => {
      mounted = false;
      controller.current?.abort();
      background.current?.abort();
    };
  }, [refresh]);
  useEffect(() => {
    const handler = () => {
      if (
        document.visibilityState === "visible" &&
        !actionLock.current &&
        !queue.current?.hasPending
      )
        void refresh().catch(() =>
          setConnectionError("Connection lost. Your last saved game is safe."),
        );
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [refresh]);
  const action = useCallback(
    async (actions: unknown[], label?: string) => {
      await initialization.current;
      const requestId = activeRequest.current;
      if (requestId) {
        foregroundSequence.current++;
        activeRequest.current = null;
        draftRequest.current = null;
        controller.current?.abort();
        actionLock.current = false;
        setBusy(null);
        pendingCancellation.current = cancelRequest(requestId)
          .then(async () => {
            const response = await api("state");
            await queue.current?.replace(response.state);
          })
          .catch(() => {
            notify(
              "Could not confirm cancellation. Your next save will check the latest position.",
            );
          });
      }
      background.current?.abort();
      try {
        if (!queue.current)
          throw new Error(
            "Your workspace could not be loaded. Reconnect and try again.",
          );
        await queue.current.enqueue(actions, label);
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : "That action could not be applied.",
        );
      }
    },
    [notify],
  );
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (queue.current?.hasPending || remoteSavingRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    const retry = () => {
      void queue.current?.retry();
    };
    window.addEventListener("beforeunload", warn);
    window.addEventListener("online", retry);
    return () => {
      window.removeEventListener("beforeunload", warn);
      window.removeEventListener("online", retry);
    };
  }, []);
  const send = async (text: string) => {
    if (actionLock.current) return false;
    // Explicit slash commands are documented shortcuts, never a pretend language model.
    const slash: Record<string, unknown[]> = {
      "/board": [{ type: "workspace.present", workspace: "board" }],
      "/play": [{ type: "game.create", mode: "play" }],
      "/games": [{ type: "workspace.present", workspace: "library" }],
      "/practice": [{ type: "practice.create", source: "curriculum" }],
      "/settings": [{ type: "workspace.present", workspace: "settings" }],
    };
    if (slash[text.toLowerCase()]) {
      await action(slash[text.toLowerCase()]);
      return Boolean(queue.current);
    }
    actionLock.current = true;
    const sequence = ++foregroundSequence.current;
    background.current?.abort();
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setBusy("Thinking with your position");
    setLocalMessages([
      { id: crypto.randomUUID(), role: "user", text, at: Date.now() },
    ]);
    const requestId =
      draftRequest.current?.text === text
        ? draftRequest.current.id
        : crypto.randomUUID();
    draftRequest.current = { text, id: requestId };
    activeRequest.current = requestId;
    try {
      await initialization.current;
      if (!queue.current)
        throw new Error(
          "Your workspace could not be loaded. Reconnect and try again.",
        );
      await pendingCancellation.current;
      await flush();
      await pendingRemote.current.catch(() => {});
      abort.signal.throwIfAborted();
      const task = remoteRequest(
        "chat",
        {
          text,
          requestId,
          expectedRevision: stateRef.current.revision,
          ...(selected ? { selectedSquare: selected } : {}),
        },
        abort.signal,
        (next) => {
          if (sequence === foregroundSequence.current) {
            activeRequest.current = null;
            controller.current = null;
            actionLock.current = false;
            setBusy(null);
            setLocalMessages([]);
            accept(next);
          }
        },
      );
      pendingRemote.current = task;
      const response = await task;
      if (sequence !== foregroundSequence.current) return true;
      accept(response.state);
      setLocalMessages([]);
      draftRequest.current = null;
      return true;
    } catch (e) {
      if (
        sequence === foregroundSequence.current &&
        e instanceof Error &&
        e.name !== "AbortError"
      ) {
        setLocalMessages((current) => [
          ...current,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            text: e.message,
            at: Date.now(),
          },
        ]);
      }
      return false;
    } finally {
      if (sequence === foregroundSequence.current) {
        activeRequest.current = null;
        actionLock.current = false;
        setBusy(null);
      }
    }
  };
  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => {
      document.documentElement.style.setProperty(
        "--visual-height",
        `${viewport?.height ?? window.innerHeight}px`,
      );
      document.documentElement.style.setProperty(
        "--visual-top",
        `${viewport?.offsetTop ?? 0}px`,
      );
    };
    update();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
    };
  }, []);
  const activeGame = state.games.find((g) => g.id === state.activeGameId);
  useEffect(() => {
    if (
      !ready ||
      saveStatus.pending > 0 ||
      remoteSaving ||
      busy ||
      actionLock.current ||
      !activeGame ||
      activeGame.mode !== "play" ||
      activeGame.result !== "*" ||
      replay(activeGame).turn() === activeGame.humanColor
    )
      return;
    const key = `${activeGame.id}:${activeGame.revision}:${reconnectEpoch}`;
    if (opponentKey.current === key) return;
    opponentKey.current = key;
    const abort = new AbortController();
    background.current = abort;
    let applying = false;
    const timer = setTimeout(() => {
      const task = remoteRequest(
        "opponent",
        {
          gameId: activeGame.id,
          revision: activeGame.revision,
          requestId: crypto.randomUUID(),
        },
        abort.signal,
        (next) => {
          applying = true;
          background.current = null;
          accept(next);
        },
      )
        .then((r) => accept(r.state))
        .catch((e) => {
          if (e.name !== "AbortError") {
            notify(e.message);
            if (e instanceof ApiError && e.status === 409) {
              opponentKey.current = "";
              setReconnectEpoch((value) => value + 1);
            }
          }
        });
      pendingRemote.current = task;
    }, 50);
    return () => {
      clearTimeout(timer);
      if (!applying) abort.abort();
    };
  }, [
    activeGame?.id,
    activeGame?.revision,
    saveStatus.pending,
    remoteSaving,
    reconnectEpoch,
    ready,
    busy,
    accept,
    notify,
    refresh,
    remoteRequest,
  ]);
  const settled = useCallback(async () => {
    await pendingRemote.current.catch(() => {});
    await queue.current?.flush();
  }, []);
  const live = useLiveCoach(
    queue.current?.snapshot ?? state,
    ready,
    accept,
    settled,
  );
  useEffect(() => {
    if (!activeGame?.clock.initial || activeGame.result !== "*") return;
    const timer = setInterval(() => {
      if (actionLock.current || queue.current?.hasPending) return;
      void api("clock", {})
        .then((r) => accept(r.state))
        .catch(() => {});
    }, 1000);
    return () => clearInterval(timer);
  }, [activeGame?.id, activeGame?.result, activeGame?.clock.initial, accept]);
  useEffect(() => {
    if (state.workspace) {
      const timer = setTimeout(
        () => main.current?.focus({ preventScroll: true }),
        520,
      );
      return () => clearTimeout(timer);
    }
    setCoachOpen(false);
  }, [state.workspace]);
  const stop = () => {
    const requestId = activeRequest.current;
    activeRequest.current = null;
    draftRequest.current = null;
    foregroundSequence.current++;
    actionLock.current = false;
    controller.current?.abort();
    background.current?.abort();
    window.speechSynthesis?.cancel();
    queue.current?.cancelComputation();
    setBusy(null);
    pendingCancellation.current = cancelRequest(requestId)
      .then(() => {
        notify("Request stopped. Completed saved work is preserved.");
        return refresh();
      })
      .catch(() => {
        notify(
          "Could not confirm cancellation. Reconnect to check your saved workspace.",
        );
      });
  };
  const deleteData = async () => {
    if (
      window.prompt(
        "This permanently deletes games, conversation and learning records from this workspace. Type DELETE MY DATA to confirm.",
      ) !== "DELETE MY DATA"
    )
      return;
    try {
      await flush();
      const response = await api("delete", {
        confirmation: "DELETE MY DATA",
        expectedRevision: stateRef.current.revision,
        requestId: crypto.randomUUID(),
      });
      accept(response.state);
      setLocalMessages([]);
      sessionStorage.removeItem("cretchess-draft");
      notify("Your chess data has been deleted.");
    } catch (e) {
      notify(e instanceof Error ? e.message : "Deletion failed.");
    }
  };
  const startExercise = (id: string) =>
    action([{ type: "practice.start", exerciseId: id }]);
  const nav = [
    { label: "Your board", workspace: "board", icon: GitBranch },
    { label: "Previous games", workspace: "library", icon: BookOpen },
    { label: "Practice", workspace: "practice", icon: Target },
    { label: "Training plan", workspace: "plan", icon: Clock3 },
    { label: "Learning notes", workspace: "memory", icon: Lightbulb },
    { label: "Settings & account", workspace: "settings", icon: Settings2 },
  ];
  return (
    <div
      className={`app ${state.workspace ? "has-workspace" : ""} ${coachOpen ? "coach-is-open" : ""}`}
    >
      {state.workspace && (
        <>
          <header className="app-header">
            <div className="brand-group">
              <button
                className="brand"
                aria-label="Cretchess home"
                onClick={() => action([{ type: "workspace.dismiss" }])}
              >
                <span className="brand-icon">
                  <CoachMark size={20} />
                </span>
                <span>cretchess</span>
              </button>
              <span className="header-divider" />
              <div className="menu-anchor">
                <button
                  className="workspace-picker"
                  onClick={() => setNavOpen(!navOpen)}
                  aria-expanded={navOpen}
                >
                  {nav.find((n) => n.workspace === state.workspace)?.label ??
                    "Your workspace"}
                  <ChevronDown size={14} />
                </button>
                {navOpen && (
                  <>
                    <button
                      className="menu-dismiss"
                      tabIndex={-1}
                      aria-label="Close navigation"
                      onClick={() => setNavOpen(false)}
                    />
                    <nav className="dropdown workspace-nav">
                      {nav.map((item) => (
                        <button
                          key={item.workspace}
                          onClick={() => {
                            setNavOpen(false);
                            void action([
                              {
                                type: "workspace.present",
                                workspace: item.workspace,
                              },
                            ]);
                          }}
                        >
                          <item.icon size={16} />
                          {item.label}
                        </button>
                      ))}
                    </nav>
                  </>
                )}
              </div>
            </div>
            <div className="header-right">
              <span className="save-status" role="status">
                {saveStatus.error ? (
                  <button onClick={() => void queue.current?.retry()}>
                    Not saved · Retry
                  </button>
                ) : saveStatus.pending || remoteSaving ? (
                  "Saving…"
                ) : (
                  ""
                )}
              </span>
              <button
                className="icon-button close-workspace"
                aria-label="Close workspace"
                title="Return to the canvas"
                onClick={() => action([{ type: "workspace.dismiss" }])}
              >
                <X size={20} />
              </button>
            </div>
          </header>
          <main
            ref={main}
            tabIndex={-1}
            className="workspace"
            key={state.workspace === "lesson" ? "board" : state.workspace}
          >
            <Suspense
              fallback={
                <div className="workspace-loading" role="status">
                  Opening your workspace…
                </div>
              }
            >
              <Workspace
                state={state}
                action={action}
                busy={busy ?? saveStatus.computing}
                selected={selected}
                setSelected={setSelected}
                importGame={() => setImportOpen(true)}
                openCoach={() => setCoachOpen(true)}
                provider={provider}
                identity={identity}
                refresh={refresh}
                flush={flush}
                lichess={lichess}
                live={live}
                startExercise={(id) => void startExercise(id)}
                deleteData={() => void deleteData()}
                notify={notify}
              />
            </Suspense>
          </main>
        </>
      )}
      <Composer
        state={state}
        provider={provider}
        busy={busy}
        open={coachOpen}
        setOpen={setCoachOpen}
        onSend={send}
        onAction={(a, label) => void action(a, label)}
        onImport={() => setImportOpen(true)}
        onStop={stop}
        onError={notify}
        localMessages={localMessages}
      />
      {importOpen && (
        <ImportDialog
          onClose={() => setImportOpen(false)}
          onImport={async (actions) => {
            if (!queue.current)
              throw new Error(
                "The workspace is still loading. Please try again.",
              );
            await queue.current.enqueue(actions);
            notify("Game imported. Saving in the background.");
          }}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <span>{toast}</span>
          <button
            aria-label="Dismiss notification"
            onClick={() => setToast("")}
          >
            <X size={15} />
          </button>
        </div>
      )}
      {!state.workspace && (saveStatus.pending > 0 || remoteSaving) && (
        <div className="canvas-save-status" role="status">
          {saveStatus.error ? (
            <button onClick={() => void queue.current?.retry()}>
              Changes not saved · Retry
            </button>
          ) : (
            "Saving…"
          )}
        </div>
      )}
      {connectionError && (
        <div className="connection-error" role="alert">
          <span>{connectionError}</span>
          <button
            onClick={() =>
              void refresh().catch((e) => setConnectionError(e.message))
            }
          >
            Reconnect
          </button>
        </div>
      )}
      {!ready && (
        <span className="loading-status sr-only" role="status">
          Opening your workspace…
        </span>
      )}
      {(busy || saveStatus.computing) && state.workspace && !coachOpen && (
        <div className="workspace-progress" role="status">
          <LoaderCircle size={14} className="spin" />
          {busy ?? saveStatus.computing}
          <button aria-label="Cancel current request" onClick={stop}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
